<template>
    <div v-if="!loading">
        <contract-step-one
            v-if="parsed_step===1"
            :contract="contract"
            :offers="offers"
            :working="working"
            @next_step="next_step"
            @prev_step="prev_step"
        ></contract-step-one>
        <contract-step-two
            v-else-if="parsed_step===2"
            :contract="contract"
            :working="working"
            @set_contract="set_contract"
            @set_confirmed="set_confirmed"
            @next_step="next_step"
            @prev_step="prev_step"
        ></contract-step-two>
        <contract-step-three
            v-else-if="parsed_step===3"
            :contract="contract"
            :working="working"
            @next_step="next_step"
            @prev_step="prev_step"
        ></contract-step-three>
        <contract-step-four
            v-else-if="parsed_step===4"
            :contract="contract"
            :working="working"
            @next_step="next_step"
            @prev_step="prev_step"
        ></contract-step-four>
        <contract-step-five
            v-else-if="parsed_step===5"
            :contract="contract"
            :contract_urlsafe="computed_contract_urlsafe"
            :connection_number="connection_number"
            :working="working"
            @next_step="next_step"
            @prev_step="prev_step"
        ></contract-step-five>
    </div>
    <div v-else>
        <div style="width: 100%; text-align: center; margin-top: 100px;">
            <span style="font-size: 30px;">
                <font-awesome-icon :icon="['fal', 'spinner']" spin></font-awesome-icon>
            </span>
        </div>
    </div>
</template>

<script>
import ContractStepOne from '@/components/ContractStep1.vue';
import ContractStepTwo from '@/components/ContractStep2.vue';
import ContractStepThree from '@/components/ContractStep3.vue';
import ContractStepFour from '@/components/ContractStep4.vue';
import ContractStepFive from '@/components/ContractStep5.vue';

export default {
    name: 'Contracts',
    components: {
        ContractStepOne,
        ContractStepTwo,
        ContractStepThree,
        ContractStepFour,
        ContractStepFive,
    },
    props: {
        step: {
            type: String,
            default: '1',
        },
        comma_seperated_offer_urlsafes: {
            type: String,
        },
        contract_urlsafe: {
            type: String,
        },
    },
    data() {
        return {
            loading: true,
            connection_number: null,
            confirmed_connection_number: false,
            offers: [],
            saving: false,
            working: false,
            contract: {
                urlsafe: null,

                coc_number: null,
                company_name: null,
                authorized_signatory: null,
                zipcode: null,
                house_number: null,
                house_number_addition: null,
                street_name: null,
                place: null,

                email: null,
                authorized_signatory_email: null,
                invoice_email: null,

                iban_number: null,

                phone_number: null,
                authorized_signatory_phone_number: null,

                location_info: [{
                    place: '',
                    number: '',
                }],

                original_offer: null,

                start_date: null,
                start_date_type: 'future',

                needs_connection_number_confirmation: false,
                status: 'new',

                hide_iban_field: false,
            },
        };
    },
    computed: {
        parsed_step: (self) => parseInt(self.step || 1, 10),
        offer_urlsafes: (self) => (self.comma_seperated_offer_urlsafes || '').split(','),
        has_offer_keys: (self) => self.offer_urlsafes.some((element) => !!element),
        computed_contract_urlsafe: (self) => self.contract.urlsafe || self.contract_urlsafe,
    },
    watch: {
        parsed_step: 'check_current_step',
    },
    methods: {
        check_current_step() {
            const self = this;

            if ([3].includes(self.parsed_step) && ['new'].includes(self.contract.status)) {
                self.contract.status = 'information';
            } else if ([4, 5].includes(self.parsed_step) && ['new', 'information'].includes(self.contract.status)) {
                self.contract.status = 'contract';
            } else {
                self.contract.status = 'new';
            }

            // No need to be on step 2 if you don't need to validate
            if (self.parsed_step === 2 && !self.contract.needs_connection_number_confirmation) {
                self.$emit('go_to_step', 3);
                return;
            }

            // No need to be on step 2 if you already validated.
            if (self.parsed_step === 2 && self.contract.needs_connection_number_confirmation
                && self.confirmed_connection_number) {
                self.$emit('go_to_step', 3);
                return;
            }

            const valid_info = self.validate(self.parsed_step - 1);
            const valid = valid_info[0];
            const first_invalid_step = valid_info[1];

            if (!valid && first_invalid_step !== self.parsed_step) {
                self.$emit('go_to_step', first_invalid_step);
            }
        },
        validate(validate_step) {
            const self = this;
            const step = validate_step || self.parsed_step;

            let valid = true;
            let first_invalid_step = 0;

            if (step >= 1) {
                valid = self.validate_step_1();
                first_invalid_step = 1;
            }

            if (step >= 2 && valid) {
                valid = self.validate_step_2();
                first_invalid_step = 2;
            }

            if (step >= 3 && valid) {
                valid = self.validate_step_3();
                first_invalid_step = 3;
            }

            if (step >= 4 && valid) {
                valid = self.validate_step_4();
                first_invalid_step = 4;
            }

            if (step >= 5 && valid) {
                // TODO: Validate step 5
                first_invalid_step = 5;
            }

            if (valid) {
                return [valid, null];
            }

            return [valid, first_invalid_step];
        },
        validate_step_1() {
            const self = this;
            return !!self.contract.original_offer;
        },
        validate_step_2() {
            const self = this;
            if (!self.contract.needs_connection_number_confirmation) {
                return true;
            }

            return self.confirmed_connection_number;
        },
        validate_step_3() {
            const self = this;

            const required_fields = [
                'coc_number',
                'company_name',
                'authorized_signatory',
                'zipcode',
                'house_number',
                'street_name',
                'place',
                'authorized_signatory_email',
                'authorized_signatory_phone_number',
                'invoice_email',
                'email',
                'phone_number',
            ];

            if (!self.contract || !self.contract.hide_iban_field) {
                required_fields.push('iban_number');
            }

            let valid = true;

            required_fields.forEach((key) => {
                if (key in self.contract) {
                    const value = self.contract[key];
                    if (!value) {
                        valid = false;
                    }
                } else {
                    valid = false;
                }
            });

            return valid;
        },
        validate_step_4() {
            const self = this;

            if (self.contract.location_info.length <= 0) {
                console.error('No location info')
                return false;
            }

            let valid = true;

            self.contract.location_info.forEach((location) => {
                if (!location.place) {
                    valid = false;
                    console.error('No location place:', location)
                }
            });

            const start_date = new Date(self.contract.start_date);
            const now = new Date();

            const now_ymd = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
            const start_ymd = `${start_date.getFullYear()}-${start_date.getMonth()}-${start_date.getDate()}`;

            if (start_date < now) {
            // if (start_ymd < now_ymd) {
                valid = false;
            }

            if (self.contract.start_date_type === 'future_first_of_month'
                && start_date.getDate() !== 1) {
                valid = false;
            }

            return valid;
        },
        prev_step() {
            const self = this;

            if (self.working) {
                return;
            }

            const step = self.parsed_step;

            if (step === 3
                && self.contract.needs_connection_number_confirmation
                && !self.confirmed_connection_number) {
                self.$emit('go_to_step', 2);
                return;
            }

            if (step === 3) {
                self.$emit('go_to_step', 1);
                return;
            }

            self.$emit('prev_step');
        },
        next_step() {
            const self = this;
            console.info('Next steppy')
            if (self.working) {
                console.error('Busy workin')
                return;
            }

            const step = self.parsed_step;

            const valid_info = self.validate();
            const valid = valid_info[0];

            if (!valid) {
                console.error('Invalid form')
                return;
            }

            if (step === 1 && !self.contract_urlsafe && !self.has_offer_keys) {
                self.$router.push({
                    name: 'showoffer',
                    params: {
                        step: '2',
                        comma_seperated_offer_urlsafes: [self.contract.original_offer].join(','),
                    },
                });
            } else if (step === 1
                && self.contract.needs_connection_number_confirmation
                && !self.confirmed_connection_number) {
                self.$emit('go_to_step', 2);
            } else if (step === 1) {
                self.$emit('go_to_step', 3);
            } else if (step === 4) {
                self.save_contract();
            } else {
                self.$emit('next_step');
            }
        },
        set_contract(contract) {
            const self = this;
            self.$set(self, 'contract', contract);
        },
        set_confirmed({value, connection_number}) {
            const self = this;
            self.confirmed_connection_number = value;
            self.connection_number = connection_number;
        },
        save_contract() {
            const self = this;

            if (self.saving) {
                return;
            }

            self.saving = true;
            self.working = true;

            const contract_clone = JSON.parse(JSON.stringify(self.contract));
            contract_clone.valid_until = self.$make_date_string(self.contract.valid_until);
            contract_clone.start_date = self.$make_date_string(self.contract.start_date);

            self.$recaptchaLoaded().then(() => {
                self.$recaptcha('create_contract').then((captcha_response) => {
                    self.$rest(
                        '/checkout/signup/service/user/recaptcha_verify/',
                        {
                            body: {
                                token: captcha_response,
                            },
                        },
                    ).then((captch_results) => {
                        if (captch_results.score > 0.5) {
                            self.$rest(
                                '/checkout/signup/service/user/save_contract/',
                                {
                                    body: contract_clone,
                                },
                            ).then((response) => {
                                self.saving = false;
                                self.working = false;
                                if (!response.success) {
                                    return;
                                }

                                self.contract.urlsafe = response.contract_urlsafe;

                                self.$router.push({
                                    name: 'showcontract',
                                    params: {
                                        contract_urlsafe: response.contract_urlsafe,
                                        step: String(self.parsed_step),
                                    },
                                });

                                self.$emit('next_step');
                            }, () => {
                                self.saving = false;
                            });
                        }
                    });
                });
            });
        },
        load_contract() {
            const self = this;
            return new Promise((resolve) => {
                self.$rest(
                    '/checkout/signup/service/user/get_contract/',
                    {
                        body: {
                            urlsafe: self.contract_urlsafe,
                        },
                    },
                ).then((response) => {
                    self.contract = self.$set(self, 'contract', response.model || {});
                    self.$nextTick(() => {
                        resolve();
                    });
                });
            });
        },
        load_offers() {
            const self = this;
            return new Promise((resolve) => {
                let urlsafes = self.offer_urlsafes;
                if (self.contract_urlsafe && self.contract.original_offer) {
                    urlsafes = [self.contract.original_offer];
                }

                self.$rest(
                    '/checkout/signup/service/user/get_offers/',
                    {
                        body: {urlsafes},
                    },
                ).then((response) => {
                    self.offers = self.$set(self, 'offers', response.offers || []);
                    self.$nextTick(() => {
                        resolve();
                    });
                });
            });
        },
        load() {
            const self = this;

            return new Promise((resolve) => {
                if (self.contract_urlsafe) {
                    self.load_contract()
                        .then(self.load_offers)
                        .then(() => {
                            self.loading = false;
                            resolve();
                        });
                } else {
                    self.load_offers().then(() => {
                        self.loading = false;
                        resolve();
                    });
                }
            });
        },
    },
    mounted() {
        const self = this;
        self.load().then(() => {
            self.check_current_step();
        });
    },
};
</script>
